<template>
  <div class="quote-summary container-border" data-testid="quote-summary">
    <div class="quote-summary__card">
      <div class="full-width">
        <h2 class="small-title padding-bottom-16 show-desktop">Your Quote</h2>

        <div class="price-display">
          <span class="show-mobile mobile-title">Reminder, your quote is</span>
          <div>
            <strong
              class="price-text-non-discounted"
              v-if="nonDiscountedPricePerPerson && hasValidDiscount"
              data-testid="price-non-discounted"
            >
              £{{ roundPrice(nonDiscountedPricePerPerson) }}
            </strong>
            <strong
              class="weekly-price price-text"
              data-testid="price-discounted"
            >
              £{{ roundPrice(pricePerPerson) }}
            </strong>
          </div>

          <span class="per-person-text" data-testid="per-person-text">
            Per person per week<span class="show-desktop"
              >, collected monthly by direct debit</span
            >
          </span>
          <monthly-price-info class="per-person-text" />
        </div>
      </div>
      <div class="show-mobile full-width">
        <button
          class="quote-summary__toggle "
          @click="expanded = !expanded"
          data-testid="toggle-summary"
        >
          <span class="toggle-text">
            {{
              expanded
                ? "Hide your package summary"
                : "View your package summary"
            }}
          </span>
          <span class="toggle-icon">
            <i
              class="fa-solid fa-chevron-down"
              :class="{ rotated: expanded }"
              data-testid="toggle-chevron"
            ></i>
          </span>
        </button>
      </div>
      <transition
        name="slide-fade"
        @enter="onEnter"
        @after-enter="onAfterEnter"
        @leave="onLeave"
        @after-leave="onAfterLeave"
      >
        <div
          v-if="expanded || !isMobile"
          class="quote-summary__details"
          ref="details"
          data-testid="summary-details"
        >
          <div class="divider"></div>

          <div>
            <h3 class="small-title padding-bottom-16">Property information</h3>
            <div class="details-container" data-testid="property-info">
              <div class="property-info-text">
                <div data-testid="housemate-count">
                  <i
                    v-if="housemateCount === 1"
                    class="fa-solid fa-user property-info-icon"
                  ></i>
                  <i v-else class="fa-solid fa-users" />
                  {{ housemateCount }} people
                </div>
                <div data-testid="address">
                  <i class="fa-solid fa-house-building property-info-icon" />
                  {{ address }}
                </div>
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <div>
            <h3 class="small-title padding-bottom-16">Your Utility Package</h3>
            <div class="products-container">
              <div
                class="products"
                v-for="product in filteredSelectedProducts"
                :key="product.code"
                :data-testid="`selected-product-${product.code}`"
              >
                <div class="icon-frame">
                  <img
                    class="icon"
                    :class="{
                      'icon--contents-insurance':
                        product.code === 'FREE_CONTENTS_INSURANCE_DISPLAY',
                    }"
                    v-if="product.image"
                    :src="product.image"
                    :alt="product.title"
                  />
                </div>
                <div class="product-name">{{ product.title }}</div>
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <div>
            <h3 class="small-title padding-bottom-16">Your Important Dates</h3>
            <div class="details-container">
              <div class="dates">
                <div class="date-title">Utilities start date</div>
                <div class="date-data" data-testid="important-date-start">
                  {{ tenancyStartDate | FormatDate }}
                </div>
              </div>
              <div class="dates">
                <div class="date-title">Utilities end date</div>
                <div class="date-data" data-testid="important-date-end">
                  {{ tenancyEndDate | FormatDate }}
                </div>
              </div>
              <div class="dates">
                <div class="date-title">First payment date</div>
                <div
                  class="date-data"
                  data-testid="important-date-first-payment"
                >
                  {{ tenancyStartDate | FormatDate }}
                </div>
              </div>
              <div class="dates" v-if="broadbandInstallationDate">
                <div class="date-title">Broadband install date</div>
                <div class="date-data" data-testid="important-date-broadband">
                  {{ broadbandInstallationDate | FormatDate }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import FormatDate from "@/filters/FormatDate";
import MonthlyPriceInfo from "@/components/MonthlyPriceInfo.vue";
import { insertAfterLastProductWithType } from "@/utility/insertAfterLastProductWithType";

export default {
  name: "QuoteSummary",
  components: { MonthlyPriceInfo },
  data() {
    return {
      expanded: false,
      toggleWeeklyPrice: true,
      isMobile: window.innerWidth <= 767,
    };
  },
  filters: {
    FormatDate,
  },
  methods: {
    roundPrice(price) {
      return (Math.ceil(price) / 100).toFixed(2);
    },
    onEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
      el.offsetHeight; // force reflow
      el.style.transition =
        "height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.4s";
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    onAfterEnter(el) {
      el.style.height = "";
      el.style.transition = "";
      el.style.opacity = "";
    },
    onLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
      el.offsetHeight; // force reflow
      el.style.transition =
        "height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.4s";
      el.style.height = "0";
      el.style.opacity = "0";
    },
    onAfterLeave(el) {
      el.style.height = "";
      el.style.transition = "";
      el.style.opacity = "";
    },
  },
  computed: {
    ...mapState({
      housemateCount: (state) => state.housemateCount,
      tenancyStartDate: ({ houseInformation }) => houseInformation.startDate,
      tenancyEndDate: ({ houseInformation }) => houseInformation.endDate,
      broadbandInstallationDate({ houseInformation }) {
        if (this.hasBroadband && !houseInformation.broadbandUrgent) {
          return houseInformation.broadbandDate;
        } else if (this.hasBroadband) {
          return this.tenancyStartDate;
        } else {
          return null;
        }
      },
    }),
    ...mapGetters({
      address: "addressLine1And2",
      hasBroadband: "hasBroadband",
      pricePerPerson: "pricePerPerson",
      pricePerPersonMonth: "pricePerPersonPerMonth",
      nonDiscountedPricePerPerson: "nonDiscountedPricePerPerson",
      nonDiscountedPricePerPersonMonth: "nonDiscountedPricePerPersonPerMonth",
      selectedProducts: "selectedProducts",
      hasValidDiscount: "hasValidDiscount",
      shouldShowFreeContentsInsurance: "shouldShowFreeContentsInsurance",
    }),
    filteredSelectedProducts() {
      const base = this.selectedProducts.filter((product) => {
        return !product.code.toUpperCase().includes("NO-");
      });

      if (!this.shouldShowFreeContentsInsurance) {
        return base;
      }

      const freeContentsInsuranceDisplay = {
        code: "FREE_CONTENTS_INSURANCE_DISPLAY",
        type: "Free Contents Insurance",
        title: "Contents Insurance",
        optional: false,
        image: "/icon-contents-insurance-alt.svg",
      };

      return insertAfterLastProductWithType(
        base,
        "Water",
        freeContentsInsuranceDisplay,
      );
    },
  },
  mounted() {
    this.handleResize = () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 767;
      if (wasMobile && !this.isMobile) {
        // Switched to desktop, always show details
        this.expanded = true;
      }
      if (!wasMobile && this.isMobile) {
        // Switched to desktop, always show details
        this.expanded = false;
      }
    };
    window.addEventListener("resize", this.handleResize);
    // On mount, if desktop, always show details
    if (!this.isMobile) {
      this.expanded = true;
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
/* Layout and Container Styles */
.quote-summary {
  border: 1px solid $brand-orange;
  border-radius: $border-radius-small;
  background: $white;
  padding: $spacing-22;
  margin: 0 auto;
}

.padding-bottom-16 {
  padding-bottom: 16px;
}

.container-border {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $spacing-22;
  border-radius: $border-radius-medium;
  border: 1px solid $grey-mid;
  background: $white;
  padding: $spacing-22;
}

.quote-summary__card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $spacing-22;
  align-self: stretch;
}

.quote-summary__details {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: $spacing-22;
}

/* Toggle Button Styles */
.quote-summary__toggle {
  background: none;
  border: none;
  color: $brand-blue;
  font-weight: $font-bold;
  cursor: pointer;
  margin-top: $spacing-22;
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  max-width: 90vw;
  gap: $spacing-xs;
  flex-wrap: nowrap;
  margin: 0 auto;
}

.toggle-text {
  font-size: 16px;
  flex: 1 1 0;
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  text-align: center;
  text-decoration: underline;
}

.toggle-icon,
.quote-summary__toggle i {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: $font-regular;
}

/* Typography and Section Titles */
.small-title {
  color: $brand-blue;
  font-size: $quote-subtitle-font-size;
  font-weight: $font-bold;
  margin: 0;
}

.mobile-title {
  color: $text;
  align-self: stretch;
  text-align: center;
  font-size: $subtitle-font-size-mob;
  font-style: normal;
  font-weight: $font-regular;
  line-height: normal;
}

/* Price Display */
.price-display {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
}

.price-text {
  color: $brand-blue;
  text-align: center;
  font-size: 32px;
  font-weight: $font-bold;
  vertical-align: middle;
}

.price-text-non-discounted {
  color: $grey-dark;
  text-align: center;
  font-size: 24px;
  font-weight: $font-medium;
  text-decoration: line-through;
  vertical-align: middle;
}

.per-person-text {
  color: $grey-text;
  text-align: center;
  font-size: $small-font-size;
  font-weight: $font-regular;
  line-height: 150%;
  display: inline;
  @include media(">=medium") {
    white-space: nowrap;
  }
}

/* Property Info and Details */
.property-info-text {
  color: $grey-text;
  font-size: $product-summary-font-size;
  font-weight: $font-regular;
  line-height: 150%;
  display: flex;
  align-items: center;
  gap: $spacing-16;
  width: 100%;
  @include media("<medium") {
    flex-direction: column;
    align-items: flex-start;
  }
}

.details-container {
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;
  width: 100%;
}

.products-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* Dates and Products */
.dates {
  display: flex;
  justify-content: space-between;
  gap: $spacing-xs;
  width: 100%;
}

.date-title,
.date-data {
  color: $text;
  font-size: $small-font-size;
  line-height: 150%;
}

.date-data {
  font-weight: $font-bold;
  text-align: right;
  min-width: 105px;
}

.products {
  display: flex;
  align-items: center;
  gap: $spacing-xs;
  width: 100%;
}

.icon-frame {
  display: flex;
  width: 50px;
  height: 50px;
  padding: 5px 8px;
  justify-content: center;
  align-items: center;
}

.icon {
  width: 50px;
  height: 50px;

  &--contents-insurance {
    width: 40px;
    height: 40px;
  }
}

.product-name {
  font-size: $product-summary-font-size;
  font-weight: $font-bold;
  display: flex;
  align-items: center;
}

.property-info-icon {
  padding-right: $spacing-xs;
  width: 20px;
  color: $text;
  text-align: center;
  font-size: $small-font-size;
  font-style: normal;
  font-weight: $font-regular;
  line-height: 150%;
}

/* Responsive and Utility Classes */
.show-desktop {
  display: inline;
}

.show-mobile {
  display: none;
}

.full-width {
  width: 100%;
}

/* Responsive Overrides */
@include media("<medium") {
  .show-desktop {
    display: none;
  }
  .show-mobile {
    display: block;
  }
  .quote-summary__toggle {
    justify-content: center;
  }
  .toggle-text {
    min-width: 0;
    text-align: center;
  }
}

/* Responsive Overrides */
@include media("<small") {
  .show-desktop {
    display: none;
  }
  .show-mobile {
    display: block;
  }
  .quote-summary__toggle {
    justify-content: center;
  }
  .toggle-text {
    min-width: 0;
    text-align: center;
  }
  .property-info-text {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

.toggle-icon i {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toggle-icon i.rotated {
  transform: rotate(180deg);
}

/* Animation */
.slide-fade-enter-active,
.slide-fade-leave-active {
  overflow: hidden;
}

.divider {
  border-bottom: 1px solid #e5e7eb;
  margin: 0;
}
</style>
